import { DestroyRef, DOCUMENT, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { ToastService } from '@rma/generic/ui/toast';
import { catchError, filter, interval, map, merge, of, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PromptRefreshService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly swUpdate = inject(SwUpdate);
  private readonly toastService = inject(ToastService);
  private readonly zone = inject(NgZone);

  private initialised = false;

  public initialise() {
    if (this.initialised) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      const fifteenMiuntes = 15 * 60 * 1000;
      const interval$ = interval(fifteenMiuntes);
      const newVersion$ = this.getNewVersionCheck();
      const manualCheck$ = interval$.pipe(switchMap(() => this.swUpdate.checkForUpdate()));

      merge(newVersion$, manualCheck$)
        .pipe(
          catchError(() => of(false)),
          filter((newVersion) => newVersion),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => this.toastService.showSuccessToastWithButton(() => this.applyUpdate(), 'Update', 'New version available.', true));
      this.initialised = true;
    });
  }

  private applyUpdate(): void {
    this.swUpdate
      .activateUpdate()
      .then(() => this.document.location.reload())
      .catch((error) => {
        console.error('Failed to apply updates:', error);
        this.document.location.reload();
      });
  }

  private getNewVersionCheck() {
    return this.swUpdate.versionUpdates.pipe(
      map((evt) => {
        switch (evt.type) {
          case 'VERSION_READY':
            return true;
          default:
          case 'VERSION_DETECTED':
          case 'VERSION_INSTALLATION_FAILED':
            return false;
        }
      }),
    );
  }
}
